import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const PALETTE = [
  '#2563eb', '#7c3aed', '#db2777', '#0d9488', '#ea580c',
  '#4f46e5', '#65a30d', '#0369a1', '#dc2626', '#059669',
];

export interface QueryResult {
  source: string;
  kind: 'collection' | 'timeseries';
  dimension?: { key: string; label: string; type: 'category' | 'time' } | null;
  measure?: { key: string; label: string; format: string };
  measures?: { key: string; label: string; format: string }[];
  data: Array<Record<string, number | string>>;
}

const fmtDate = (value: string | number) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
};

const CatalogChart: React.FC<{ result: QueryResult; chartType: string; height?: number }> = ({
  result,
  chartType,
  height = 280,
}) => {
  const data = result.data || [];
  const isTime = result.kind === 'timeseries' || result.dimension?.type === 'time';
  const noDim = result.kind === 'collection' && !result.dimension;

  if (!data.length) {
    return <div className="alert alert-info mb-0">No data for this selection.</div>;
  }

  // Single scalar → big number card.
  if (noDim || chartType === 'number') {
    const value = Number(data[0]?.value || 0);
    return (
      <div className="catalog-number" style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
        <div style={{ fontSize: '3rem', fontWeight: 600, lineHeight: 1 }}>{value.toLocaleString()}</div>
        <div className="text-muted">{result.measure?.label || ''}</div>
      </div>
    );
  }

  if (isTime) {
    const seriesKeys = result.measures?.length
      ? result.measures.map((m) => ({ key: m.key, label: m.label }))
      : [{ key: 'value', label: result.measure?.label || 'Value' }];
    const stacked = chartType === 'stackedArea';
    const Chart = chartType === 'bar' ? BarChart : chartType === 'area' || chartType === 'stackedArea' ? AreaChart : LineChart;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <Chart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="date" tickFormatter={fmtDate} minTickGap={24} />
          <YAxis allowDecimals={false} />
          <Tooltip labelFormatter={fmtDate} formatter={(v: number) => v.toLocaleString()} />
          <Legend />
          {seriesKeys.map((s, i) => {
            const color = PALETTE[i % PALETTE.length];
            if (chartType === 'bar') {
              return <Bar key={s.key} dataKey={s.key} name={s.label} fill={color} stackId={stacked ? 'a' : undefined} />;
            }
            if (chartType === 'area' || chartType === 'stackedArea') {
              return (
                <Area
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={color}
                  fill={color}
                  fillOpacity={0.3}
                  stackId={stacked ? 'a' : undefined}
                />
              );
            }
            return <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={color} strokeWidth={2} dot={false} />;
          })}
        </Chart>
      </ResponsiveContainer>
    );
  }

  // Categorical breakdown.
  if (chartType === 'pie') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="label" outerRadius={Math.max(60, height / 2 - 30)} label>
            {data.map((_, i) => (
              <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(v: number) => v.toLocaleString()} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="label" />
        <YAxis allowDecimals={false} />
        <Tooltip formatter={(v: number) => v.toLocaleString()} />
        <Bar dataKey="value" name={result.measure?.label || 'Value'}>
          {data.map((_, i) => (
            <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default CatalogChart;
